import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { VerificationSuccessBanner, VerificationErrorBanner } from "@/components/verification-banner";

/**
 * Handles email verification after a user clicks a link in an email
 */
export default function VerificationConfirmation() {
  const [location] = useLocation();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Extract the token from the URL
  const params = new URLSearchParams(location.split("?")[1]);
  const token = params.get("token");

  useEffect(() => {
    async function verifyEmail() {
      if (!token) {
        setStatus("error");
        setErrorMessage("No verification token provided");
        return;
      }

      try {
        const response = await apiRequest("GET", `/api/auth/verify-email?token=${token}`);
        
        if (response.ok) {
          setStatus("success");
        } else {
          const data = await response.json();
          setStatus("error");
          setErrorMessage(data.message || "Failed to verify email address");
        }
      } catch (error) {
        console.error("Verification error:", error);
        setStatus("error");
        setErrorMessage("An error occurred during verification. Please try again later.");
      }
    }

    verifyEmail();
  }, [token]);

  return (
    <div className="container max-w-md mx-auto p-4 min-h-screen flex items-center justify-center">
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-2xl">Email Verification</CardTitle>
          <CardDescription>
            {status === "loading" ? "Processing your verification..." : 
             status === "success" ? "Your email has been verified!" : 
             "There was a problem with verification."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === "loading" ? (
            <div className="flex flex-col items-center justify-center py-6">
              <Loader2 className="h-16 w-16 animate-spin text-primary mb-4" />
              <p className="text-center text-sm text-muted-foreground">
                Please wait while we verify your email address...
              </p>
            </div>
          ) : status === "success" ? (
            <div className="flex flex-col items-center justify-center py-6">
              <VerificationSuccessBanner />
              <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
              <p className="text-center">
                Thank you! Your email has been successfully verified. You now have full access to all features.
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6">
              <VerificationErrorBanner message={errorMessage} />
              <XCircle className="h-16 w-16 text-red-500 mb-4" />
              <p className="text-center">
                {errorMessage || "There was a problem verifying your email. Please try again or contact support."}
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button asChild>
            <Link href="/">Return to Homepage</Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}